"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { CartItem } from "@/types/orders/cart";
import { calculateLeadTime, formatPrice } from "@/lib/utils";
import { PackageIcon, ClockIcon } from "lucide-react";
import Table from "@/components/table";
import { Separator } from "@/components/ui/separator";

// Utility to group identical cart items by product_id, tier_id, and decal_number
export function groupCartItems(
  items: CartItem[]
): (CartItem & { _quantity?: number })[] {
  const grouped: Record<string, CartItem & { _quantity?: number }> = {};

  items.forEach((item) => {
    // Create a unique key for grouping
    const key = [item.product_id, item.tier_id, item.decal_number || ""].join(
      "|"
    );

    if (!grouped[key]) {
      grouped[key] = { ...item, _quantity: 1 };
    } else {
      // Sum the quantities
      grouped[key]._quantity = (grouped[key]._quantity || 1) + 1;
      // Optionally, merge attachments if needed (here we just concat arrays)
      if (item.attachments && grouped[key].attachments) {
        grouped[key].attachments = [
          ...grouped[key].attachments!,
          ...item.attachments,
        ];
      }
    }
  });

  return Object.values(grouped);
}

// Split items for accordion logic, but only group as accordion if there are multiple decals for a part_number
export function splitItemsForAccordion(
  items: (CartItem & { _quantity?: number })[]
) {
  const withDecal: Record<string, (CartItem & { _quantity?: number })[]> = {};
  const withoutDecal: (CartItem & { _quantity?: number })[] = [];
  const withAttachments: (CartItem & { _quantity?: number })[] = [];

  items.forEach((item) => {
    // Check if item has attachments first
    if (item.attachments && item.attachments.length > 0) {
      withAttachments.push(item);
    } else if (item.decal_number) {
      if (!withDecal[item.part_number]) {
        withDecal[item.part_number] = [];
      }
      withDecal[item.part_number].push(item);
    } else {
      withoutDecal.push(item);
    }
  });

  // Move single-decal groups to withoutDecal
  Object.keys(withDecal).forEach((partNumber) => {
    if (withDecal[partNumber].length === 1) {
      withoutDecal.push(withDecal[partNumber][0]);
      delete withDecal[partNumber];
    }
  });

  return { withDecal, withoutDecal, withAttachments };
}

// Utility to generate a URL for the product page
export function generateProductUrl(
  item: CartItem & { _quantity?: number }
): string {
  // Use a standard format for all product URLs
  let url = `/${item.part_number.split("-")[0].toLowerCase()}/products/${item.part_number}`;

  // Add query parameters as needed
  const params = new URLSearchParams();

  // Add decal parameter for custom products
  if (item.decal_number) {
    params.set("decal", item.decal_number);
  }

  // Append parameters if any exist
  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

// Safe calculation of item price accounting for undefined quantity
export function getItemTotal(item: CartItem & { _quantity?: number }) {
  // Default to 1 if _quantity is undefined, null, or NaN
  const quantity =
    typeof item._quantity === "number" && !isNaN(item._quantity)
      ? item._quantity
      : 1;

  // For kit products, only use the main item price without adding attachment prices
  // A kit is identified by either an explicit is_kit flag or by having attachments
  const isKit =
    "is_kit" in item
      ? item.is_kit === true
      : item.attachments &&
        Array.isArray(item.attachments) &&
        item.attachments.length > 0;

  let itemTotal = item.price * quantity;

  // Only add attachment prices for non-kit products
  // For kits, the price already includes all items
  if (!isKit && item.attachments && Array.isArray(item.attachments)) {
    itemTotal += item.attachments.reduce((attachmentTotal, attachment) => {
      const attachmentQty =
        typeof attachment.quantity === "number" && !isNaN(attachment.quantity)
          ? attachment.quantity
          : 1;
      return attachmentTotal + attachment.price * attachmentQty;
    }, 0);
  }

  return itemTotal;
}

// Define type for formatted item data including details
interface FormattedItemData {
  image: React.ReactNode;
  description: React.ReactNode;
  decalNumber: string;
  partNumber: string;
  qty: number;
  price: number;
  formattedPrice: string;
  details?: FormattedItemData[];
  [key: string]:
    | React.ReactNode
    | string
    | number
    | FormattedItemData[]
    | undefined;
}

// Custom hook to measure table width
function useTableWidth() {
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Update on resize
    const observer = new ResizeObserver(updateWidth);
    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  return { tableRef, tableWidth };
}

interface OrderSummaryTableProps {
  cartItems: CartItem[];
  className?: string;
  shipping?: {
    id: string;
    name: string;
    price: number;
    carrier: string;
    estimatedDays: {
      latest: number;
      earliest: number;
    };
    shippingServiceId?: string;
  };
  // Column visibility props
  showImage?: boolean;
  showPartNumber?: boolean;
  showQuantity?: boolean;
  showPrice?: boolean;
  showActions?: boolean;
  // Display options
  showLeadTimes?: boolean;
  showPackageInfo?: boolean;
  showItemIds?: boolean;
  hideAttachmentPrices?: boolean;
  // Custom actions for items
  renderItemActions?: (
    item: CartItem & { _quantity?: number }
  ) => React.ReactNode;
}

export function OrderSummaryTable({
  cartItems,
  className = "",
  shipping,
  showImage = true,
  showPartNumber = true,
  showQuantity = true,
  showPrice = true,
  showActions = false,
  showLeadTimes = false,
  showPackageInfo = false,
  showItemIds = false,
  hideAttachmentPrices = false,
  renderItemActions,
}: OrderSummaryTableProps) {
  // Use grouped items for initial state
  const [currentItems, setCurrentItems] = useState<
    (CartItem & { _quantity?: number })[]
  >(groupCartItems(cartItems));

  // Split items for grouping (items with same part number but different decals)
  const { withDecal, withoutDecal, withAttachments } =
    splitItemsForAccordion(currentItems);

  // Calculate lead time info if needed
  const [leadTimeInfo, setLeadTimeInfo] = useState(() =>
    calculateLeadTime(groupCartItems(cartItems) as CartItem[])
  );

  // Update items when cart items change
  useEffect(() => {
    const grouped = groupCartItems(cartItems);
    setCurrentItems(grouped);
    setLeadTimeInfo(calculateLeadTime(grouped as CartItem[]));
  }, [cartItems]);

  // Get table width for responsive columns
  const { tableRef, tableWidth } = useTableWidth();

  // Calculate breakpoints for table width
  const showDecalNumber = tableWidth > 800;
  const showPartNumberResponsive = showPartNumber && tableWidth > 650;
  const showQuantityResponsive = showQuantity && tableWidth > 500;
  const showPriceResponsive = showPrice && tableWidth > 400;

  // Create responsive description based on screen size and visible columns
  const createResponsiveDescription = (
    item: CartItem & { _quantity?: number },
    isAttachment = false,
    isDecalItem = false
  ) => {
    const productTitle = (
      <div className='font-medium break-words whitespace-normal'>
        {!isAttachment ? (
          <Link href={generateProductUrl(item)} className='hover:underline'>
            {isDecalItem && item.decal_number
              ? `Decal: ${item.decal_number}`
              : item.title}
          </Link>
        ) : (
          item.title
        )}
      </div>
    );

    return (
      <div className='flex flex-col gap-1'>
        {productTitle}

        {/* Display Item ID if enabled and available */}
        {showItemIds && item.item_id && (
          <div className='text-primary text-sm mt-1'>ID: {item.item_id}</div>
        )}

        {!isDecalItem && !showDecalNumber && item.decal_number && (
          <div className='text-muted-foreground text-sm'>
            Decal Number: {item.decal_number}
          </div>
        )}

        {!showPartNumberResponsive && (
          <div className='text-muted-foreground text-sm'>
            Part Number: {item.part_number}
          </div>
        )}

        {!showQuantityResponsive && (
          <div className='text-muted-foreground text-sm'>
            Qty: {item._quantity || 1}
          </div>
        )}

        {!showPriceResponsive && (
          <div className='text-muted-foreground text-sm'>
            Price: {formatPrice(getItemTotal(item))}
          </div>
        )}
      </div>
    );
  };

  // Format cart items for Table component
  const formattedItems: FormattedItemData[] = [];

  // 1. Add regular items without decals
  withoutDecal.forEach((item) => {
    formattedItems.push(createFormattedItem(item));
  });

  // 2. Add items with attachments
  withAttachments.forEach((item) => {
    formattedItems.push(createFormattedItem(item, true));
  });

  // 3. Add grouped items with the same part number but different decals
  Object.keys(withDecal).forEach((partNumber) => {
    const decalItems = withDecal[partNumber];
    const firstItem = decalItems[0];

    // Calculate total quantity and price for the group
    const totalQuantity = decalItems.reduce(
      (sum, item) => sum + (item._quantity || 1),
      0
    );
    const groupTotal = decalItems.reduce(
      (sum, item) =>
        sum + getItemTotal(item as CartItem & { _quantity?: number }),
      0
    );

    // Create parent item for the part number group
    const parentItem: FormattedItemData = {
      image: createImageComponent(firstItem),
      description: (
        <div className='font-medium break-words whitespace-normal'>
          <Link
            href={generateProductUrl(firstItem)}
            className='hover:underline'
          >
            {firstItem.title}
          </Link>
          <div className='text-sm text-muted-foreground mt-1'>
            {decalItems.length} variations
          </div>
        </div>
      ),
      decalNumber: "",
      partNumber: partNumber,
      qty: totalQuantity,
      price: groupTotal,
      formattedPrice: formatPrice(groupTotal),
      // Add details for the expandable content
      details: decalItems.map((item) => createFormattedDecalItem(item)),
    };

    formattedItems.push(parentItem);
  });

  // Helper function to create image component
  function createImageComponent(item: CartItem) {
    return (
      <div className='w-16 h-16 bg-black/5 flex items-center justify-center overflow-hidden rounded-lg'>
        {item.image ? (
          <Image
            src={item.image}
            alt={item.title}
            width={64}
            height={64}
            style={{ objectFit: "contain" }}
          />
        ) : (
          <div className='h-full w-full flex items-center justify-center text-muted-foreground text-xs'>
            No image
          </div>
        )}
      </div>
    );
  }

  // Helper function to create formatted item data
  function createFormattedItem(
    item: CartItem & { _quantity?: number },
    withAttachments = false
  ): FormattedItemData {
    const totalPrice = getItemTotal(item);

    // Create item data
    const itemData: FormattedItemData = {
      image: createImageComponent(item),
      description: createResponsiveDescription(item),
      decalNumber: item.decal_number || "",
      partNumber: item.part_number,
      qty: item._quantity || 1,
      price: totalPrice,
      formattedPrice: formatPrice(totalPrice),
    };

    // Add actions if provided
    if (showActions && renderItemActions) {
      itemData.actions = renderItemActions(item);
    }

    // Format attachments as details if the item has attachments
    if (withAttachments && item.attachments && item.attachments.length > 0) {
      itemData.details = item.attachments.map((attachment) => {
        const attachmentPrice = hideAttachmentPrices
          ? 0
          : attachment.price * (attachment.quantity || 1);
        const attachmentData: FormattedItemData = {
          image: createImageComponent(attachment),
          description: createResponsiveDescription(attachment, true),
          decalNumber: attachment.decal_number || "",
          partNumber: attachment.part_number,
          qty: attachment.quantity || 1,
          price: attachmentPrice,
          formattedPrice: hideAttachmentPrices
            ? "-"
            : formatPrice(attachmentPrice),
        };

        // Add actions if provided
        if (showActions && renderItemActions) {
          attachmentData.actions = renderItemActions(attachment);
        }

        return attachmentData;
      });
    }

    return itemData;
  }

  // Helper function to create formatted decal item
  function createFormattedDecalItem(
    item: CartItem & { _quantity?: number }
  ): FormattedItemData {
    return {
      image: createImageComponent(item),
      description: createResponsiveDescription(item, false, true),
      decalNumber: item.decal_number || "",
      partNumber: item.part_number,
      qty: item._quantity || 1,
      price: getItemTotal(item),
      formattedPrice: formatPrice(getItemTotal(item)),
      ...(showActions && renderItemActions
        ? { actions: renderItemActions(item) }
        : {}),
    };
  }

  // Currency formatter
  const formatCurrency = (value: number) => {
    return formatPrice(value);
  };

  // Dynamic columns based on table width
  const columns = [
    ...(showImage
      ? [
          {
            id: "image",
            header: "Image",
            accessorKey: "image",
            className: "w-[100px] flex-none",
          },
        ]
      : []),
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      className: "min-w-0 whitespace-normal break-words",
    },
    ...(showDecalNumber
      ? [
          {
            id: "decalNumber",
            header: "Decal Number",
            accessorKey: "decalNumber",
            className: "text-right pr-4",
          },
        ]
      : []),
    ...(showPartNumberResponsive
      ? [
          {
            id: "partNumber",
            header: "Part Number",
            accessorKey: "partNumber",
            className: "w-[150px] flex-none text-right pr-4",
          },
        ]
      : []),
    ...(showQuantityResponsive
      ? [
          {
            id: "qty",
            header: "Qty",
            accessorKey: "qty",
            className: "w-[80px] flex-none text-center pr-4",
            isNumeric: true,
          },
        ]
      : []),
    ...(showPriceResponsive
      ? [
          {
            id: "price",
            header: "Price",
            accessorKey: "formattedPrice",
            summaryAccessorKey: "price",
            summaryFormatter: formatCurrency,
            className: "w-[120px] flex-none text-right pr-4",
            isNumeric: true,
          },
        ]
      : []),
    ...(showActions
      ? [
          {
            id: "actions",
            header: "Actions",
            accessorKey: "actions",
            className: "w-[100px] flex-none text-center",
          },
        ]
      : []),
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      <div ref={tableRef}>
        <h2 className='text-xl font-semibold mb-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <PackageIcon className='h-6 w-6' />
              Order Summary
            </div>
            {showPriceResponsive && (
              <span className='text-sm text-muted-foreground pr-10'>
                excl. GST
              </span>
            )}
          </div>
        </h2>

        <Table
          columns={columns}
          data={formattedItems}
          expandedContentKey='details'
          showSummary={true}
          summaryText='Total'
        />
      </div>

      {showLeadTimes && (
        <>
          <Separator className='my-6' />
          {/* Lead Times Section */}
          <div className='mt-6'>
            <h2 className='text-xl font-semibold mb-4'>
              <div className='flex items-center gap-2'>
                <ClockIcon className='h-6 w-6' />
                Lead Times
              </div>
            </h2>
            <div className='space-y-2 text-sm'>
              <div className='flex justify-between font-medium'>
                <span>Estimated Production Time:</span>
                <span>{leadTimeInfo.totalLeadTime} business days</span>
              </div>
              {leadTimeInfo.breakdown.map((item, index) => (
                <div
                  key={index}
                  className='flex justify-between text-muted-foreground'
                >
                  <span>{item.brand}:</span>
                  <span>{item.description}</span>
                </div>
              ))}
              <p className='text-xs text-muted-foreground mt-2'>
                {leadTimeInfo.note}
              </p>
            </div>
          </div>
        </>
      )}

      {showPackageInfo && (
        <>
          <Separator className='my-6' />
          <div>
            <h2 className='text-xl font-semibold mb-4'>
              <div className='flex items-center gap-2'>
                <PackageIcon className='h-6 w-6' />
                Package Information
              </div>
            </h2>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <p className='text-sm font-medium'>Total Weight</p>
                <p className='text-sm text-muted-foreground'>
                  {currentItems
                    .reduce((sum, item) => sum + (item.shipping_weight || 0), 0)
                    .toFixed(2)}{" "}
                  kg
                </p>
              </div>

              {currentItems.length > 0 && (
                <div className='space-y-2'>
                  <p className='text-sm font-medium'>Package Dimensions</p>
                  {currentItems.map((item, index) => {
                    const length = item.shipping_length || item.length;
                    const width = item.shipping_width || item.width;
                    const height = item.shipping_height || item.height;
                    const weight = item.shipping_weight || 0;

                    if (length && width && height) {
                      return (
                        <p
                          key={index}
                          className='text-sm text-muted-foreground'
                        >
                          Package {index + 1}: {length}cm × {width}cm × {height}
                          cm
                          {weight > 0 ? ` - ${weight.toFixed(2)}kg` : ""}
                        </p>
                      );
                    }
                    return null;
                  })}

                  {currentItems.length > 1 && (
                    <div className='mt-2 text-xs text-muted-foreground'>
                      <p>
                        Shipping costs are calculated based on the total weight
                        and dimensions of all items. For large quantities, items
                        may be split into multiple packages for shipping.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Display shipping information if provided */}
      {shipping && (
        <>
          <Separator className='my-6' />
          <div>
            <h2 className='text-xl font-semibold mb-4'>
              <div className='flex items-center gap-2'>
                <PackageIcon className='h-6 w-6' />
                Shipping Information
              </div>
            </h2>
            <div className='space-y-2'>
              <div className='flex justify-between'>
                <span>Method:</span>
                <span>{shipping.name}</span>
              </div>
              <div className='flex justify-between'>
                <span>Carrier:</span>
                <span>{shipping.carrier}</span>
              </div>
              <div className='flex justify-between'>
                <span>Estimated Delivery:</span>
                <span>
                  {shipping.estimatedDays.earliest} -{" "}
                  {shipping.estimatedDays.latest} days
                </span>
              </div>
              {showPrice && (
                <div className='flex justify-between'>
                  <span>Shipping Cost:</span>
                  <span>{formatPrice(shipping.price)}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OrderSummaryTable;
